/**
 * One card is a WHOLE PLATE, not a dish (#1282).
 *
 * That is the difference between this and every other meal app: the card names
 * the main, its sides, what each person's plate actually looks like, and
 * whether tomorrow's lunch is already solved. A card that says only "Chili" is
 * a card you cannot decide from.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { twoPlates, type OfferedMeal } from "../api/twoPlates";

const BALANCE_LABEL: Record<string, string> = {
  protein: "Protein",
  veg: "Veg",
  grain: "Grain",
  fruit: "Fruit",
};

function plateColour(index: number) {
  return index % 2 === 0 ? "var(--color-plate-a)" : "var(--color-plate-b)";
}

export function MealCard({ meal }: { meal: OfferedMeal }) {
  const bothFine = meal.plates.every((p) => p.skippedSides.length === 0);
  const missing = meal.balance.filter((b) => !b.covered);

  return (
    <article className="bg-surface border-line flex flex-col gap-4 rounded-2xl border p-5">
      <header>
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-2xl font-semibold leading-tight tracking-tight">{meal.name}</h2>
          <Star meal={meal} />
        </div>
        <div className="text-muted mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
          <span>{meal.activeMinutes} min hands-on</span>
          {meal.slowCooker && (
            <span className="text-plate-a">
              slow cooker · starts in the morning
            </span>
          )}
          <span>{bothFine ? "fits you both" : "one plate skips a side"}</span>
          <span>{meal.makesLunch ? "makes lunch" : "cook for two"}</span>
        </div>
      </header>

      <div className="flex flex-wrap gap-1.5">
        {meal.balance.map((b) => (
          <span
            key={b.tag}
            className="rounded-full border px-2.5 py-1 text-[11px]"
            style={
              b.covered
                ? { borderColor: "var(--color-line)", color: "var(--color-muted)" }
                : { borderColor: "var(--color-warn)", color: "var(--color-warn)" }
            }
          >
            {BALANCE_LABEL[b.tag]}
            {!b.covered && " missing"}
          </span>
        ))}
      </div>

      <div>
        <p className="text-muted text-xs uppercase tracking-wide">The meal</p>
        <p className="mt-1.5 text-sm">
          {meal.anchor}
          {meal.sides.length > 0 && (
            <span className="text-muted"> with {meal.sides.join(", ")}</span>
          )}
        </p>
      </div>

      <div className="flex flex-col gap-3">
        {meal.plates.map((plate, i) => (
          <div key={plate.eaterId} className="flex gap-3">
            <span
              aria-hidden
              className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ background: plateColour(i) }}
            />
            <div className="min-w-0">
              <p className="text-sm font-medium">{plate.eaterName}</p>
              <p className="text-muted mt-0.5 text-sm">
                {plate.baseOnly ? "the meal as it is" : plate.forks.join("; ")}
              </p>
              {plate.skippedSides.length > 0 && (
                <p className="text-muted mt-0.5 text-xs">
                  skips {plate.skippedSides.join(" and ")}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>

      {meal.note && (
        // Written after cooking it last time. Shown here so the choice is made
        // knowing what happened.
        <p className="text-plate-a text-xs">Last time: {meal.note}</p>
      )}

      {meal.varietyNote && (
        <p className="text-muted text-xs">{meal.varietyNote}</p>
      )}

      {(missing.length > 0 || meal.styleNotes.length > 0 || meal.droppedSides.length > 0) && (
        <footer className="border-line flex flex-col gap-1 border-t pt-3">
          {missing.length > 0 && (
            <p className="text-warn text-xs">
              No {missing.map((m) => BALANCE_LABEL[m.tag].toLowerCase()).join(" or ")} on this
              plate — add a side or let the next night carry it.
            </p>
          )}
          {meal.droppedSides.map((d) => (
            <p key={d.name} className="text-muted text-xs">
              {d.reason}
            </p>
          ))}
          {meal.styleNotes.map((n) => (
            <p key={n} className="text-muted text-xs">
              {n}
            </p>
          ))}
        </footer>
      )}
    </article>
  );
}

/**
 * The star.
 *
 * ⚠️ Optimistic, and stops the pointer from reaching the swipe handler — a
 * gesture that starts on a control must never throw the card.
 */
function Star({ meal }: { meal: OfferedMeal }) {
  const queryClient = useQueryClient();
  const [on, setOn] = useState(meal.starred);

  const save = useMutation({
    mutationFn: (next: boolean) => twoPlates.markMeal(meal.mealId, { starred: next }),
    onError: (_e, next) => setOn(!next),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["deck"] }),
  });

  return (
    <button
      onClick={(e) => { e.stopPropagation(); const next = !on; setOn(next); save.mutate(next); }}
      onPointerDown={(e) => e.stopPropagation()}
      aria-pressed={on}
      aria-label={on ? `Unstar ${meal.name}` : `Star ${meal.name}`}
      className="shrink-0 rounded-lg px-2 py-1 text-xl leading-none"
      style={{ color: on ? "var(--color-plate-a)" : "var(--color-line)" }}
    >
      {on ? "★" : "☆"}
    </button>
  );
}
